import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Download, Search, Filter
} from "lucide-react";
import { useGetList } from "ra-core";
import type { CustomerInvoice, Company } from "../types";

const PAYMENT_STATUSES = ["Paid", "Partial", "Pending", "Overdue"] as const;

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toCsv(rows: InvoiceRow[]): string {
  const header = ["Invoice Reference", "Customer", "Total Amount", "Invoice Date", "Due Date", "Payment Status"];
  const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((inv) => [
    inv.reference,
    inv.customer_name || inv.customer?.name || "Unknown",
    inv.total_amount,
    inv.date || "",
    inv.due_date || "",
    getPaymentStatus(inv),
  ].map(escape).join(","));
  return [header.map(escape).join(","), ...lines].join("\n");
}

interface InvoiceRow extends CustomerInvoice {
  customer?: Company;
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return "₹0.00";
  return `₹${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getPaymentStatus(invoice: CustomerInvoice): "Paid" | "Partial" | "Pending" | "Overdue" {
  let status = (invoice as any).payment_status || invoice.status || "draft";
  if (invoice.due_date && status !== "paid" && status !== "draft") {
    const dueDate = new Date(invoice.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dueDate < today) status = "overdue";
  }
  if (status === "paid") return "Paid";
  if (status === "partial") return "Partial";
  if (status === "overdue") return "Overdue";
  return "Pending";
}

export function SalesList() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const { data: invoices, isLoading, error } = useGetList<InvoiceRow>("customer_invoices", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "date", order: "DESC" },
  });

  const filteredInvoices = invoices?.filter(inv =>
    (searchTerm === "" ||
      inv.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()))
    && (statusFilter.length === 0 || statusFilter.includes(getPaymentStatus(inv)))
  );

  const toggleStatusFilter = (status: string) => {
    setStatusFilter(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const handleExport = () => {
    const csv = toCsv(filteredInvoices ?? []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `global-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleAll = () => {
    if (!filteredInvoices) return;
    setSelected(selected.length === filteredInvoices.length ? [] : filteredInvoices.map(d => String(d.id)));
  };

  const toggleOne = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-destructive font-medium">Failed to load sales ledger: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-5 gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Global Sales Ledger</h1>
          <p className="text-muted-foreground text-sm mt-1.5">
            {filteredInvoices?.length ?? 0} invoice(s) from database
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="h-9 shadow-sm font-semibold" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" /> Export Data
          </Button>
        </div>
      </div>

      <Card className="shadow-none overflow-hidden">
        <CardHeader className="border-b flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold">Transaction History</CardTitle>
          <div className="flex items-center gap-3">
             {selected.length > 0 && (
               <Badge className="bg-primary text-primary-foreground shadow-sm px-3 py-1 animate-in slide-in-from-right-2">{selected.length} Selected</Badge>
             )}
             <div className="relative max-w-xs hidden md:block">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
               <Input
                 placeholder="Search invoices..."
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 className="h-8 pl-8 text-xs w-[200px] shadow-none"
               />
             </div>
             <DropdownMenu>
               <DropdownMenuTrigger asChild>
                 <Button variant="outline" size="sm" className="h-8 text-xs font-semibold">
                   <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters
                   {statusFilter.length > 0 && <Badge className="ml-1.5 h-4 px-1.5 text-[10px] bg-primary text-primary-foreground">{statusFilter.length}</Badge>}
                 </Button>
               </DropdownMenuTrigger>
               <DropdownMenuContent align="end">
                 {PAYMENT_STATUSES.map((status) => (
                   <DropdownMenuCheckboxItem
                     key={status}
                     checked={statusFilter.includes(status)}
                     onCheckedChange={() => toggleStatusFilter(status)}
                     onSelect={(e) => e.preventDefault()}
                   >
                     {status}
                   </DropdownMenuCheckboxItem>
                 ))}
               </DropdownMenuContent>
             </DropdownMenu>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border w-10">
                  <Checkbox
                    checked={filteredInvoices && selected.length === filteredInvoices.length && filteredInvoices.length > 0}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">Invoice Reference</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">Customer</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border text-right">Total Amount</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">Invoice Date</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground border-r border-border">Due Date</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices?.map((invoice) => {
                const status = getPaymentStatus(invoice);
                return (
                  <tr
                    key={invoice.id}
                    className={`${selected.includes(String(invoice.id)) ? 'bg-primary/5' : 'even:bg-muted/20'} hover:border-b border-border transition-colors`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-border align-middle">
                      <Checkbox
                        checked={selected.includes(String(invoice.id))}
                        onCheckedChange={() => toggleOne(String(invoice.id))}
                      />
                    </td>
                    <td
                      className="px-4 py-3.5 text-sm border-r border-border font-mono font-bold text-primary hover:underline cursor-pointer align-middle"
                      onClick={() => navigate(`/customer_invoices/${invoice.id}/show`)}
                    >
                      {invoice.reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border font-semibold text-foreground align-middle">
                      {invoice.customer_name || invoice.customer?.name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-right font-bold text-foreground align-middle">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-muted-foreground font-medium align-middle">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-border text-muted-foreground font-medium align-middle">
                      {formatDate(invoice.due_date)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Badge className={`
                        ${status === "Paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900 dark:text-emerald-200" : ""}
                        ${status === "Partial" ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200" : ""}
                        ${status === "Pending" ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900 dark:text-amber-200" : ""}
                        ${status === "Overdue" ? "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900 dark:text-rose-200" : ""}
                        font-bold border shadow-none
                      `}>
                        {status}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!filteredInvoices || filteredInvoices.length === 0) && (
            <div className="p-8 text-center text-muted-foreground">
              No invoices found. Create invoices from the Invoices module.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
